import { useEffect, useMemo, useRef, useState } from "react";
import { FileUploadRef } from "../../../shared/components/FileUpload";

import { useGetUsers } from "../../../shared/hooks";
import { usePhase } from "../../projectOverview/hooks/usePhase";

import { useCreate, useGetQueryDetails, useUpdate } from "../mutation";
import { useSearchProjects } from "../../projects/mutations";

import { AutocompleteOption } from "../../../shared/components/ControlledAutocomplete";

export const useRaiseQuery = ({ id, projectId, editStatus }: { id: null | string; projectId: string | undefined; editStatus: boolean }) => {

    const fileUploadRef = useRef<FileUploadRef | null>(null);

    const create = useCreate();
    const update = useUpdate();

    const searchProjects = useSearchProjects();
    const phaseData = usePhase();

    const { queryDetails, isFetching, isPending, isError, removeCache } = useGetQueryDetails({ queryId: id! });

    const { data: usersData, isLoading: isLoadingUsers } = useGetUsers();
    const userOptions = useMemo(() => {
        if (!usersData?.data) return [];
        return usersData.data
            .filter((u) => u.is_active === "1")
            .map((u) => ({
                value: u.user_id,
                label: `${u.first_name} ${u.last_name}`,
            }));
    }, [usersData]);

    const [query, setQuery] = useState<{
        title: null | string,
        category: null | string,
        type: 'Project' | 'Portfolio' | 'Programme',
        priority: null | string,
        description: null | string,
        phase: null | string,
        assigned: null | string,
        query_category: null | string,
        project: null | AutocompleteOption,
        portfolio: null | AutocompleteOption,
        programme: null | AutocompleteOption,
    }>({
        title: null,
        category: null,
        type: 'Project',
        priority: null,
        description: null,
        phase: null,
        assigned: null,
        query_category: 'project',
        project: null,
        portfolio: null,
        programme: null,
    });

    const activeProjectId = useMemo(() => {
        return query.project?.id ?? projectId;
    }, [query.project?.id, projectId]);

    const handleUpdate = (key: string, value: any) => {
        setQuery({
            ...query,
            [key]: value
        })
    };

    const params = () => {
        const payload: any = {
            title: query.title,
            description: query.description,
            category: query.category,
            priority: query.priority,
            user_id: query.assigned,
            type: query.type
        };
        if (query.project) {
            payload.project_id = query.project.id;
        };
        if (query.portfolio) {
            payload.portfolio_id = query.portfolio.id;
        };
        if (query.phase) {
            payload.project_tree_id = query.phase;
        };
        if (query.programme) {
            payload.programme_id = query.programme.id;
        };
        return payload;
    };

    const onSave = async () => {
        let response;
        if (editStatus && id) {
            response = await update.mutateAsync({ queryId: id, payload: params() });
        } else {
            response = await create.mutateAsync({ payload: params() });
        }
        if (fileUploadRef.current) {
            fileUploadRef.current.setId(response.data.id);
            setTimeout(() => {
                fileUploadRef.current?.uploadAllFiles();
            }, 0);
        }
        removeCache();
    };

    const fetchProjectDetails = async () => {
        const params: Record<string, any> = {
            project_id: projectId
        };
        const response = await searchProjects.mutateAsync({ params });
        setQuery({
            ...query,
            project: {
                id: response.data[0].id,
                label: response.data[0].title
            }
        });
    };

    useEffect(() => {
        if (projectId && !editStatus)
            fetchProjectDetails();
    }, [projectId]);

    useEffect(() => {
        if (editStatus && queryDetails) {
            setQuery({
                title: queryDetails.title,
                category: queryDetails.category,
                type: queryDetails.type,
                priority: queryDetails.priority,
                description: queryDetails.description,
                phase: queryDetails.project_tree_id,
                assigned: queryDetails.user_id,
                query_category: queryDetails.query_category,
                project: queryDetails.project_id ? { id: queryDetails.project_id, label: queryDetails.project_title } : null,
                portfolio: queryDetails.portfolio_id ? { id: queryDetails.portfolio_id, label: queryDetails.portfolio_title } : null,
                programme: queryDetails.programme_id ? { id: queryDetails.programme_id, label: queryDetails.programme_title } : null,
            });
        }
    }, [editStatus, queryDetails, isFetching, isPending, isError]);

    useEffect(() => {
        if (activeProjectId)
            phaseData.mutateAsync(activeProjectId);
    }, [activeProjectId])

    return {
        userOptions, isLoadingUsers,
        query,
        handleUpdate,
        onSave,
        fileUploadRef,
        phaseData,
        isPending: create.isPending || update.isPending || phaseData.isPending,
    };

};